import PropTypes from 'prop-types';
import { Box, Card, Stack, Typography, Button, Skeleton } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Iconify from '@/components/iconify';
import useWebhookExecutionDetails from '@/hooks/useWebhookExecutionDetails';
import WebhookExecutionDetailsModal from '@/sections/@dashboard/apps/WebhookExecutionDetailsModal/WebhookExecutionDetailsModal';

const ACTIVITY_STATUS_STYLES = (theme) => ({
  success: {
    icon: 'solar:check-circle-outline',
    color: theme.palette.success.main,
  },
  warning: {
    icon: 'solar:danger-circle-outline',
    color: theme.palette.warning.light,
  },
  error: {
    icon: 'solar:close-circle-outline',
    color: theme.palette.error.main,
  },
});

export default function RecentActivitiesSection({
  activities,
  onViewAll = undefined,
  isLoading = false,
}) {
  const theme = useTheme();
  const activityStatusStyles = ACTIVITY_STATUS_STYLES(theme);
  const { execution, isOpen, isResending, openDetails, closeDetails, resend } =
    useWebhookExecutionDetails();

  return (
    <Box>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 'none',
          bgcolor: theme.palette.background.dark,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            bgcolor: theme.palette.background.muted,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            Atividades recentes
          </Typography>
          <Button variant="contained" onClick={onViewAll} disabled={!onViewAll}>
            Ver Todos
          </Button>
        </Box>

        <Box>
          {isLoading && activities.length === 0 &&
            Array.from({ length: 2 }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  px: 2.5,
                  py: 2.5,
                  borderTop:
                    index === 0 ? 'none' : `1px dashed ${alpha(theme.palette.text.secondary, 0.32)}`,
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={72} height={20} />
                  <Skeleton variant="text" width={180} height={24} />
                  <Skeleton variant="text" width="28%" height={20} />
                </Stack>
              </Box>
            ))}

          {!isLoading && activities.length === 0 && (
            <Box sx={{ px: 2.5, py: 4, bgcolor: theme.palette.background.paper }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Nenhuma atividade recente encontrada.
              </Typography>
            </Box>
          )}

          {!isLoading && activities.map((item, index) => {
            const style = activityStatusStyles[item.type];

            return (
              <Box
                key={item.id}
                sx={{
                  px: 2.5,
                  py: 2.5,
                  display: 'flex',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1.5, md: 2 },
                  borderTop:
                    index === 0 ? 'none' : `1px dashed ${alpha(theme.palette.text.secondary, 0.32)}`,
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    width: '100%',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    rowGap: 1,
                  }}
                >
                  <Box>
                    <Iconify
                      icon={style.icon}
                      width={24}
                      height={24}
                      sx={{ color: style.color }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, minWidth: { md: 80 } }}
                  >
                    {item.timeAgo}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      minWidth: { md: 160 },
                    }}
                  >
                    {item.event}
                  </Typography>

                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: theme.palette.text.primary,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {item.webhook}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={{ xs: 'space-between', md: 'flex-end' }}
                  spacing={1.5}
                  sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: style.color,
                      minWidth: { md: 120 },
                      textAlign: { xs: 'left', md: 'right' },
                    }}
                  >
                    {item.statusText}
                  </Typography>

                  <Button
                    size="small"
                    variant="text"
                    onClick={() => openDetails(item)}
                    aria-label={`Ver detalhes da execução de ${item.event} em ${item.webhook}`}
                    sx={{
                      textTransform: 'none',
                      fontSize: 13,
                      fontWeight: 600,
                      minWidth: 'auto',
                      px: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Ver detalhes
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Card>

      <WebhookExecutionDetailsModal
        open={isOpen}
        onClose={closeDetails}
        onResend={resend}
        isResending={isResending}
        execution={execution}
      />
    </Box>
  );
}

RecentActivitiesSection.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      type: PropTypes.oneOf(['success', 'warning', 'error']).isRequired,
      timeAgo: PropTypes.string.isRequired,
      event: PropTypes.string.isRequired,
      webhook: PropTypes.string.isRequired,
      statusText: PropTypes.string.isRequired,
      raw: PropTypes.object,
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  onViewAll: PropTypes.func,
};
